if (!customElements.get('collection-controls')) {
  customElements.define('collection-controls', class extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.addEventListener('click', this.onClick.bind(this), options);
      this.addEventListener('change', this.onChange.bind(this), options);
      this.addEventListener('input', this.onRangeInput.bind(this), options);
      this.addEventListener('submit', this.onSubmit.bind(this), options);
      this.addEventListener('close', this.onClose.bind(this), { ...options, capture: true });
      document.addEventListener('click', (event) => {
        const sort = this.querySelector('.collection-toolbar__sort');
        if (sort && !sort.contains(event.target)) sort.open = false;
      }, options);
      this.addEventListener('keydown', (event) => {
        const sort = this.querySelector('.collection-toolbar__sort[open]');
        if (event.key === 'Escape' && sort) {
          sort.open = false;
          sort.querySelector('summary').focus();
          event.preventDefault();
        }
      }, options);
      window.addEventListener('popstate', () => this.render(new URL(location.href), false), options);
      document.addEventListener('shopify:block:select', (event) => {
        if (this.querySelector('dialog')?.contains(event.target)) this.openDrawer();
      }, options);
    }

    disconnectedCallback() {
      this.events.abort();
      this.request?.abort();
      clearTimeout(this.priceTimer);
      document.documentElement.classList.remove('collection-filters-open');
    }

    openDrawer(restoreFocus = true) {
      const drawer = this.querySelector('dialog');
      if (!drawer || drawer.open) return;
      this.querySelector('.collection-toolbar__sort')?.removeAttribute('open');
      drawer.showModal();
      this.querySelector('[data-filters-open]').setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('collection-filters-open');
      if (restoreFocus) drawer.querySelector('[data-filters-close]').focus();
    }

    onClose(event) {
      if (!event.target.matches('.collection-filter-drawer')) return;
      document.documentElement.classList.remove('collection-filters-open');
      const trigger = this.querySelector('[data-filters-open]');
      trigger?.setAttribute('aria-expanded', 'false');
      trigger?.focus({ preventScroll: true });
    }

    onClick(event) {
      if (event.target.closest('[data-filters-open]')) return this.openDrawer();
      const drawer = this.querySelector('dialog');
      if (event.target.closest('[data-filters-close]')) return drawer.close();
      if (event.target === drawer) {
        const bounds = drawer.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) drawer.close();
        return;
      }
      const filter = event.target.closest('.js-collection-side-filter');
      if (filter) {
        event.preventDefault();
        const input = filter.querySelector('input');
        if (input.disabled) return;
        input.checked = !input.checked;
        this.submit(filter.closest('form'));
        return;
      }
      const remove = event.target.closest('.js-current-filter');
      if (remove && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
        event.preventDefault();
        this.render(new URL(remove.href));
      }
    }

    onRangeInput(event) {
      if (!event.target.matches('.filter-range__slider--input')) return;
      const group = event.target.closest('.js-price-range');
      const values = Array.from(group.querySelectorAll('input[type="range"]'), input => Number(input.value)).sort((a, b) => a - b);
      group.querySelectorAll('input[type="number"]').forEach((input, index) => { input.value = values[index]; });
    }

    onChange(event) {
      if (!event.target.closest('[data-collection-drawer-form]')) return;
      if (event.target.matches('input[type="number"], input[type="range"]')) {
        const input = event.target;
        if (input.type === 'number' && input.value !== '') {
          if (!Number.isFinite(input.valueAsNumber)) return;
          input.value = Math.min(Number(input.max), Math.max(Number(input.min), input.valueAsNumber));
        }
        clearTimeout(this.priceTimer);
        const form = input.form;
        this.priceTimer = setTimeout(() => {
          if (form.isConnected) this.submit(form);
        }, 350);
      }
    }

    onSubmit(event) {
      if (!event.target.matches('[data-collection-drawer-form], [data-collection-sort-form]')) return;
      event.preventDefault();
      this.submit(event.target, event.submitter);
    }

    submit(form, submitter) {
      const data = new FormData(form);
      if (submitter?.name) data.set(submitter.name, submitter.value);
      const url = new URL(location.href);
      for (const key of [...url.searchParams.keys()]) {
        if (key.startsWith('filter.') || key === 'sort_by' || key === 'page') url.searchParams.delete(key);
      }
      for (const [key, value] of data) {
        if (value !== '') {
          if (key.startsWith('filter.')) url.searchParams.append(key, value);
          else url.searchParams.set(key, value);
        }
      }
      this.render(url);
    }

    async render(url, updateHistory = true) {
      clearTimeout(this.priceTimer);
      this.request?.abort();
      const request = this.request = new AbortController();
      const grid = this.querySelector('#CollectionProductGrid');
      grid.setAttribute('aria-busy', 'true');
      const sectionUrl = new URL(url);
      sectionUrl.searchParams.set('section_id', this.dataset.sectionId);
      try {
        const response = await fetch(sectionUrl, { signal: request.signal });
        if (!response.ok) throw new Error('Collection request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextGrid = html.getElementById('CollectionProductGrid');
        if (!nextGrid) throw new Error('Collection content missing');
        const drawerOpen = this.querySelector('dialog')?.open;
        const drawerScroll = this.querySelector('.collection-filter-drawer__content')?.scrollTop;
        const focused = document.activeElement;
        const focusId = focused?.id;
        const filterInputId = focused?.closest('.js-collection-side-filter')?.querySelector('input')?.id;
        const sortFocused = !!focused?.closest('.collection-toolbar__sort');
        const controlFocused = !!focused?.closest('.js-current-filter');
        grid.innerHTML = nextGrid.innerHTML;
        if (updateHistory) history.pushState({}, '', url);
        if (drawerOpen) {
          this.openDrawer(false);
          this.querySelector('.collection-filter-drawer__content').scrollTop = drawerScroll;
          const target = document.getElementById(filterInputId || focusId);
          (target?.closest('.js-collection-side-filter') || target || this.querySelector('[data-filters-close]')).focus({ preventScroll: true });
        } else if (sortFocused) {
          this.querySelector('.collection-toolbar__sort summary')?.focus({ preventScroll: true });
        }
        if (!drawerOpen && controlFocused) {
          this.querySelector('[data-filters-open], .collection-toolbar__sort summary')?.focus({ preventScroll: true });
        }
        window.Shopify?.theme?.quickview?.init();
        window.WAU?.ProductGridVideo?.init();
      } catch (error) {
        if (error.name !== 'AbortError') location.assign(url);
      } finally {
        if (this.request === request) grid.removeAttribute('aria-busy');
      }
    }
  });
}
